import { P2GButtonGroup, P2GButtonGroupItem, P2GDropdown, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { useContext, useEffect, useState } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  AddNewItemAction,
  ArrangeReturnCollectionAction,
  UpdateFilterAction
} from '../../../lib/context/SmartSendContextModels';
import { getNamedFilter } from '../../../lib/helpers/DefaultFilters';
import { EditType } from '../item/EditType';

import { ItemOrderPanel } from '../panels/ItemOrderPanel';
import { ItemOrderTrackingPanel } from '../panels/ItemOrderTrackingPanel';
import SetMarketplaceAction, { ItemActionType } from '../item/SetMarketplaceAction';
import { ItemOrderActionsPanel } from '../panels/ItemOrderActionsPanel';
import { GenericEditProps } from './Edit';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { uid } from '../../../lib/helpers/uid';
import ItemRefunds from './ItemRefunds';
import { GetExternalTrackingNumber } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { EventSections } from '../../../lib/events/EventLogger';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import SelectOrCopy from '../item/SelectOrCopy';
import { P2GText } from '@parcel2go/core-ui';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useCreateItemsHelper from '../../../lib/helpers/useItemCreateHelper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SmartSendSubType } from '~/apps/smartsend/lib/consts/SmartSendSubType';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import ItemOrderLabelsPanel from './ItemOrderLabelsPanel';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props extends GenericEditProps {
  getRawItem: (id: string) => SmartSendItemApiModel;
  onCancel: () => void;
  onRaiseEnquiry: () => void;
}

export default function SmartSendOrderDetails(props: props) {
  const { goTo } = useGoto();
  const { createItemsHelper } = useCreateItemsHelper();
  const { getItemActions, getTracking } = useSmartSendApi();
  const ctx = useContext(SmartSendContext);
  const [item, setItem] = useState({ ...props.item });
  const { emit } = useEventEmitter<EditEventType>();
  const trackingNumber = GetExternalTrackingNumber(props.item);
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const isXs = useP2GMediaQueryOnly('xs');

  useEffect(() => {
    track({
      type: 'edit_item_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.BookedItems, 'order-details-panel']),
        action: props.editType?.toLowerCase()
      }
    });
  }, [props.editType]);

  const changeTab = (tab: EditType) => {
    emit(Consts.Edit, { Id: item.Id, Action: tab });
  };

  const arrangeReturn = async (item: SmartSendItemApiModel) => {
    ctx.dispatch(new ArrangeReturnCollectionAction([item.Id]));
    goTo(RoutingConstants.SmartSend);
  };

  const buyAgain = async (item: SmartSendItemApiModel) => {
    const itemToAdd: SmartSendItemApiModel = JSON.parse(JSON.stringify(item));

    itemToAdd.Id = null;
    itemToAdd.Source = { Id: 'manual', Name: 'Manual', AccountId: null };
    itemToAdd.Status = 'Active';
    itemToAdd.MarketplaceStatus = 'Sold';
    itemToAdd.BookingDetails = null;
    itemToAdd.TrackingSummary = null;

    itemToAdd.MarketplaceOrderLines.forEach((i) => {
      i.Source = itemToAdd.Source;
      i.Id = null;
      i.MarketplaceOrderId = null;
      i.MarketplaceOrderLineId = null;
      i.InternalReference = null;
      i.CustomerReference = null;
      i.Status = itemToAdd.Status;
      i.MarketplaceStatus = itemToAdd.MarketplaceStatus;
    });

    itemToAdd.Description = itemToAdd.MarketplaceOrderLines[0]?.Product?.Description || 'New Item';

    itemToAdd.Tags.push('Reorder');

    createItemsHelper(
      [{ id: uid(), item: itemToAdd, raw: [] }],
      (items) => {
        const item = items[0];
        ctx.dispatch(new AddNewItemAction(item, !item.Service?.Id));
        ctx.dispatch(
          new UpdateFilterAction(SmartSendSubType, {
            ...ctx.state.filters[SmartSendSubType].filter,
            CurrentFilter: getNamedFilter('Post_Create')
          })
        );
      },
      () => {},
      () => {
        goTo(RoutingConstants.SmartSend);
        props.onClose();
      }
    );
  };

  const setAction = (type: ItemActionType) => {
    SetMarketplaceAction(ctx.dispatch, props.item.Id, type);
    const i = props.getRawItem(props.item.Id);
    setItem(Object.assign(item, i));
  };

  return (
    <SidePanelSections>
      <ItemRefunds bookingDetails={item?.BookingDetails} />

      {isXs || SettingsCache.settings.Features.includes('DropDownPanelNavigation') ? (
        <P2GDropdown
          id="View Details:"
          onChange={changeTab}
          options={[
            { value: 'SMARTSEND-ORDER-ACTIONS', text: t('ACTIONS_TAB', { defaultValue: 'Actions' }) },
            {
              value: 'SMARTSEND-ORDER-DETAILS',
              text: t('ORDER_DETAILS_TAB', { defaultValue: 'Order Details' })
            },
            SettingsCache.settings.Features.includes('Tracking') && {
              value: 'SMARTSEND-ORDER-TRACKING',
              text: t('TRACKING_TAB', { defaultValue: 'Tracking' })
            }
          ]}
          value={props.editType}
        />
      ) : (
        <P2GButtonGroup fullWidth>
          <P2GButtonGroupItem value="SMARTSEND-ORDER-ACTIONS" selected={props.editType} onClick={changeTab}>
            {t('ACTIONS_TAB', { defaultValue: 'Actions' })}
          </P2GButtonGroupItem>
          <P2GButtonGroupItem value="SMARTSEND-ORDER-DETAILS" selected={props.editType} onClick={changeTab}>
            {t('ORDER_DETAILS_TAB', { defaultValue: 'Order Details' })}
          </P2GButtonGroupItem>
          {SettingsCache.settings.Features.includes('Tracking') && (
            <P2GButtonGroupItem value="SMARTSEND-ORDER-TRACKING" selected={props.editType} onClick={changeTab}>
              {t('TRACKING_TAB', { defaultValue: 'Tracking' })}
            </P2GButtonGroupItem>
          )}
        </P2GButtonGroup>
      )}

      {trackingNumber && (
        <SidePanelSection title={t('TRACKING_NUMBER_SUBTITLE', { defaultValue: 'Tracking Number' })}>
          <P2GText>
            {trackingNumber} <SelectOrCopy code={trackingNumber} />
          </P2GText>
        </SidePanelSection>
      )}

      {props.editType === 'SMARTSEND-ORDER-LABELS' && (
        <ItemOrderLabelsPanel item={props.item} editType={props.editType} />
      )}

      {props.editType === 'SMARTSEND-ORDER-DETAILS' && (
        <ItemOrderPanel key={item.Id} item={item} close={props.onClose} setAction={setAction} />
      )}

      {props.editType === 'SMARTSEND-ORDER-ACTIONS' && (
        <ItemOrderActionsPanel
          key={item.Id}
          item={item}
          close={props.onClose}
          onCancel={props.onCancel}
          onRaiseEnquiry={props.onRaiseEnquiry}
          buyAgain={() => buyAgain(item)}
          arrangeReturn={() => arrangeReturn(item)}
          setAction={setAction}
          actions={() => getItemActions(item.Id).then((r) => (r.success ? r.result : null))}
        />
      )}

      {props.editType === 'SMARTSEND-ORDER-TRACKING' && (
        <ItemOrderTrackingPanel
          recipientIsoCode={item.Recipient?.CountryIsoCode}
          senderIsoCode={item.Sender?.CountryIsoCode}
          trackingDetails={item.TrackingSummary}
          trackingNumber={trackingNumber}
          getTracking={() => {
            return getTracking(item.Id).then((r) => {
              return r.success ? r.result : { Tracking: [], Trackable: true, Tags: [], Links: {} };
            });
          }}
          bookingDetails={item.BookingDetails}
          close={props.onClose}
        />
      )}
    </SidePanelSections>
  );
}
